import React, { useState, useEffect, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import Grid from '@mui/material/Grid';

import FabAdd from 'components/FabAdd';
import EnhancedTable from 'components/EnhancedTable';
import DateRangePickerField from 'components/DateRangePickerField';
import SearchBox from 'components/SearchBox';
import ContextStore from 'modules/context';
import { firebaseV8 } from 'modules/firebaseV8';
import TransferServiceView from './TransferServiceView';
import CompanyFilter from 'components/CompanyFilter';
import SimpleTableToolbar from 'components/SimpleTableToolbar';

function TransferServiceList() {
  const { formatMessage } = useIntl()
  const { setBreadcrumbs, currentCompany } = useContext(ContextStore)
  const userRights = useSelector(state => state.userRights)
  const [startDate, setStartDate] = useState(dayjs().subtract(2, 'months').format('YYYY-MM') + '-01')
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [transferServices, setTransferServices] = useState([])
  const [currentFilter, setCurrentFilter] = useState(null);
  const userMapping = useSelector(state => state.users.data)
  const [customerMapping, setCustomerMapping] = useState({});

  const filteredCurrentData = filterCurrentData()

  useEffect(() => {
    const unsubscribe = firebaseV8().collection('transferServices').where('date', '>=', startDate).where('date', '<=', endDate).onSnapshot(snapshot => {
      const newData = []
      snapshot.forEach(doc => {
        const data = { ...doc.data(), id: doc.id }
        newData.push(data)
      });

      setTransferServices(newData.filter(t => t.source === currentCompany))
    }, err => { })
    return () => unsubscribe()
  }, [startDate, endDate, currentCompany]);

  useEffect(() => {
    const unsubscribe = firebaseV8().collection('customers').onSnapshot(snapshot => {
      const customerMapping = {}
      snapshot.forEach(doc => {
        const data = { ...doc.data(), id: doc.id }
        customerMapping[doc.id] = data
      });
      setCustomerMapping(customerMapping)
    }, err => { })
    return () => unsubscribe()
  }, []);

  useEffect(() => {
    setBreadcrumbs([{
      text: formatMessage({ id: 'sideMenu.services.transfer' })
    }])

  }, []);

  const filterItems = [
    { name: 'sn' },
    { name: 'date' },
    { name: 'customer' },
    { name: 'createdBy' },
  ].map(i => { i.text = formatMessage({ id: `transferService.filter.${i.name}` }); return i })

  const headerCells = [
    { text: 'sn', sort: 'sn' },
    { text: 'date', sort: 'date' },
    { text: 'createdBy' },
    { text: 'from' },
    { text: 'to' },
  ].map(c => { c.text = formatMessage({ id: `transferService.table.header.${c.text}` }); return c })

  const rowCells = [
    { field: 'sn' },
    { field: 'date' },
    { field: 'createdBy' },
    { field: 'from' },
    { field: 'to' },
  ]

  const onFilterChanged = (name, text) => {
    if (text !== '') {
      setCurrentFilter({ name, text })
    } else {
      setCurrentFilter(null)
    }
  }

  function filterCurrentData() {
    if (!currentFilter) {
      return transferServices
    }

    let items = [...transferServices]
    if (currentFilter) {
      const lowerCaseText = currentFilter.text.toLowerCase()
      if (currentFilter.name === 'sn') {
        items = items.filter(i => i.sn.toLowerCase().includes(lowerCaseText))
      } else if (currentFilter.name === 'createdBy') {
        items = items.filter(i => userMapping[i.createdBy]?.displayName.toLowerCase().includes(lowerCaseText))
      } else if (currentFilter.name === 'date') {
        items = items.filter(i => i.date.toLowerCase().includes(lowerCaseText))
      } else if (currentFilter.name === 'customer') {
        items = items.filter(i => customerMapping[i.customer]?.name.toLowerCase().includes(lowerCaseText) || customerMapping[i.customer]?.code.toLowerCase().includes(lowerCaseText))
      }
    }

    return items
  }

  function formatData(data) {
    const newData = { ...data }
    newData.createdBy = userMapping[newData.createdBy].displayName
    newData.to = customerMapping[newData.newCustomer] ? customerMapping[newData.newCustomer].name : ''
    newData.from = customerMapping[newData.customer] ? customerMapping[newData.customer].name : ''

    return newData
  }

  return <div style={{ padding: '20px 24px 80px 24px' }}>
    {userRights.hasUserRight('transferService-create') && <FabAdd to="/services/transfer/edit/new" />}

    <SimpleTableToolbar
      title='transferService.table.title'
      toolbox={<>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={9}>
            <DateRangePickerField
              startDate={startDate}
              endDate={endDate}
              onStartDateChanged={setStartDate}
              onEndDateChanged={setEndDate}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <CompanyFilter userRight='transferService-view'/>
          </Grid>
        </Grid>
        <SearchBox filterItems={filterItems} onFilterChanged={onFilterChanged} updateUrlParams />
      </>}
    />
    <EnhancedTable
      defaultOrder="asc"
      defaultOrderField="sn"
      headerCells={headerCells}
      rowCells={rowCells}
      getExpandContent={transferService =>
        <TransferServiceView
          form={transferService}
        />
      }
      tableData={filteredCurrentData.map(i => formatData(i))}
    />
  </div>

}

export default TransferServiceList
